import { useState, useCallback, useRef, useEffect, useMemo, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import ConfirmDeleteDialog from "@/shared/components/overlay/ConfirmDeleteDialog";

import type { PlaidLinkOnSuccessMetadata } from "react-plaid-link";

import { useDeceasedUserData } from "@/shared/hooks/useDeceasedUserData";
import { useUserEstateRole } from "@/shared/hooks/useUserEstateRole";
import { useHasAuthorityDocument } from "@/shared/hooks/useHasAuthorityDocument";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { useDeceasedEINData } from "@/shared/hooks/useDeceasedEINData";
import { usePlaid } from "@/shared/hooks/usePlaid";
import { useTransactions, useUpdateTransaction } from "@/shared/hooks/useTransactions";
import { supabase } from "@/shared/lib/supabase";
import { generateTransactionsPdf } from "@/shared/utils/generateTransactionsPdf";
import { POST_LOSS_ROUTES } from "@/shared/constants/paths";

import type { PlaidAccount } from "@/shared/types/estateTypes";

import { Button } from "@/shared/components/ui/button";

import Loader from "@/shared/components/Loader";
import { AlreadyHaveEINDrawer } from "@/pages/postloss/settle-estate/components/AlreadyHaveEINDrawer";
import { AccountSetupChecklist } from "./AccountSetupChecklist";
import { PlaidPermissionsDialog } from "./PlaidPermissionsDialog";
import { PlaidLinkLauncher } from "./PlaidLinkLauncher";
import { AccountSelectorDialog } from "./AccountSelectorDialog";
import { FirstSyncLoading } from "./FirstSyncLoading";
import { ConnectedAccountCard } from "./ConnectedAccountCard";
import { ReconnectBanner } from "./ReconnectBanner";
import { FinancialSummary } from "./FinancialSummary";
import { ActivityTable, type Transaction } from "./ActivityTable";
import { TransactionDetailPanel } from "./TransactionDetailPanel";

type ConnectionStep = "idle" | "permissions" | "plaid-link" | "exchanging" | "account-selector" | "syncing";

interface EstateAccountTabProps {
  isPaidUser: boolean;
  onUpgrade: () => void;
}

export const EstateAccountTab: FC<EstateAccountTabProps> = ({ isPaidUser, onUpgrade }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { firstName, fullName } = useDeceasedUserData();
  const { hasLegalAuthority, isLoading: isRoleLoading } = useUserEstateRole();
  const { person } = useAppContext();
  const estateId = person?.estate_id ?? null;
  const { hasAuthorityDocument, isFetched: isAuthorityDocFetched } = useHasAuthorityDocument(estateId ?? undefined);
  const hasAuthority = hasLegalAuthority || hasAuthorityDocument;

  const {
    connections,
    isLoadingConnections,
    connectionsError,
    createLinkToken,
    isCreatingLinkToken,
    exchangeToken,
    refreshBalances,
    isRefreshing,
    disconnect,
    isDisconnecting,
  } = usePlaid(estateId, "estate_account");

  const [connectionStep, setConnectionStep] = useState<ConnectionStep>("idle");
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [pendingAccounts, setPendingAccounts] = useState<PlaidAccount[]>([]);
  const [pendingConnectionId, setPendingConnectionId] = useState<number | null>(null);
  const [pendingInstitution, setPendingInstitution] = useState<{
    institution_id: string;
    name: string;
    logo: string | null;
  } | null>(null);
  const [reconnectingConnectionId, setReconnectingConnectionId] = useState<number | null>(null);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [showEinDrawer, setShowEinDrawer] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [refreshResult, setRefreshResult] = useState<{ refreshed_count: number } | null>(null);
  const refreshResultTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
      if (refreshResultTimerRef.current) clearTimeout(refreshResultTimerRef.current);
    };
  }, []);

  const displayName = firstName || "the decedent";
  const estateDisplayName = fullName ? `Estate of ${fullName}` : "Estate";

  const { hasEIN: hasEin } = useDeceasedEINData(estateId ?? undefined);
  const hasEstateAccount = connections.length > 0;

  const activeConnection = connections[0];

  const [selectedTransactionId, setSelectedTransactionId] = useState<number | null>(null);

  const {
    transactions: rawTransactions,
    syncTransactions: triggerSync,
    isLoading: isLoadingTransactions,
    error: transactionsError,
  } = useTransactions(estateId, activeConnection?.id ?? null);

  const {
    updateTransaction,
    isUpdating,
  } = useUpdateTransaction(estateId, activeConnection?.id ?? null);

  const selectedTransaction = selectedTransactionId
    ? rawTransactions.find((t) => t.id === selectedTransactionId) ?? null
    : null;

  const { mappedTransactions, moneyIn, moneyOut } = useMemo(() => {
    let inTotal = 0;
    let outTotal = 0;
    const mapped: Transaction[] = rawTransactions.map((t) => {
      if (t.is_inflow) inTotal += t.amount;
      else outTotal += t.amount;
      return {
        id: t.id,
        date: t.date,
        description: t.description,
        originalDescription: t.original_description,
        category: t.category ?? undefined,
        hasReceipt: t.has_receipt,
        amount: t.amount,
        isInflow: t.is_inflow,
      };
    });
    return { mappedTransactions: mapped, moneyIn: inTotal, moneyOut: outTotal };
  }, [rawTransactions]);

  const handleConnect = useCallback((erroredConnectionId?: number) => {
    if (erroredConnectionId) {
      setReconnectingConnectionId(erroredConnectionId);
    }
    setConnectionStep("permissions");
  }, []);

  const handlePermissionsContinue = useCallback(async () => {
    try {
      const result = await createLinkToken(undefined);
      setLinkToken(result.link_token);
      setConnectionStep("plaid-link");
    } catch {
      enqueueSnackbar("Couldn't connect. Please try again.", { variant: "error" });
      setConnectionStep("idle");
    }
  }, [createLinkToken]);

  const handlePermissionsClose = useCallback((open: boolean) => {
    if (!open) {
      setConnectionStep("idle");
    }
  }, []);

  const handlePlaidSuccess = useCallback(
    async (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
      const institution = metadata.institution;
      if (!institution) {
        setConnectionStep("idle");
        return;
      }

      const institutionPayload = {
        institution_id: institution.institution_id,
        name: institution.name,
      };

      setPendingInstitution({ ...institutionPayload, logo: null });
      setConnectionStep("exchanging");

      try {
        const selectedAccountIds = metadata.accounts?.map((a) => a.id) ?? [];
        const result = await exchangeToken({
          publicToken,
          institution: institutionPayload,
          accountIds: selectedAccountIds.length > 0 ? selectedAccountIds : undefined,
        });
        setPendingInstitution({ ...institutionPayload, logo: result.institution_logo });

        if (reconnectingConnectionId && result.connection_id !== reconnectingConnectionId) {
          try {
            await disconnect(reconnectingConnectionId);
          } catch {
            // Old connection cleanup is best-effort
          }
        }
        setReconnectingConnectionId(null);

        if (result.accounts.length === 0) {
          enqueueSnackbar("No accounts found. Please try again.", { variant: "error" });
          setConnectionStep("idle");
        } else if (result.accounts.length > 1) {
          setPendingAccounts(result.accounts);
          setPendingConnectionId(result.connection_id);
          setConnectionStep("account-selector");
        } else {
          setConnectionStep("syncing");
          triggerSync(result.connection_id).catch((err: unknown) => {
            console.error("Initial transaction sync failed:", err);
          });
          syncTimerRef.current = setTimeout(() => {
            enqueueSnackbar("Connected. 1 account added.", { variant: "success" });
            setConnectionStep("idle");
            setLinkToken(null);
            setPendingInstitution(null);
          }, 2500);
        }
      } catch {
        enqueueSnackbar("Couldn't connect the account. Please try again.", { variant: "error" });
        setConnectionStep("idle");
        setReconnectingConnectionId(null);
      }
    },
    [exchangeToken, reconnectingConnectionId, disconnect, triggerSync],
  );

  const handlePlaidExit = useCallback(() => {
    setConnectionStep("idle");
    setLinkToken(null);
  }, []);

  const handleAccountSelect = useCallback(async (selectedAccountId: number) => {
    const unselected = pendingAccounts.filter((a) => a.id !== selectedAccountId);
    setConnectionStep("syncing");

    for (const account of unselected) {
      try {
        await supabase.functions.invoke(
          `financials/${estateId}/items/${account.id}`,
          { method: "DELETE" },
        );
      } catch {
        // Best-effort cleanup of unselected accounts
      }
    }

    if (pendingConnectionId) {
      triggerSync(pendingConnectionId).catch((err: unknown) => {
        console.error("Initial transaction sync failed:", err);
      });
    }

    syncTimerRef.current = setTimeout(async () => {
      await queryClient.invalidateQueries({ queryKey: ["plaidConnections"] });
      await queryClient.invalidateQueries({ queryKey: ["estate"] });
      enqueueSnackbar("Account connected", { variant: "success" });
      setConnectionStep("idle");
      setLinkToken(null);
      setPendingAccounts([]);
      setPendingConnectionId(null);
      setPendingInstitution(null);
    }, 2500);
  }, [pendingAccounts, pendingConnectionId, estateId, triggerSync]);

  const handleAccountSelectorClose = useCallback(async (open: boolean) => {
    if (!open) {
      for (const account of pendingAccounts) {
        try {
          await supabase.functions.invoke(
            `financials/${estateId}/items/${account.id}`,
            { method: "DELETE" },
          );
        } catch {
          // Best-effort cleanup
        }
      }
      if (pendingConnectionId) {
        try {
          await disconnect(pendingConnectionId);
        } catch {
          // Best-effort
        }
      }
      setPendingAccounts([]);
      setPendingConnectionId(null);
      setConnectionStep("idle");
    }
  }, [pendingAccounts, pendingConnectionId, estateId, disconnect]);

  const handleRefresh = useCallback(
    async (connectionId: number) => {
      if (refreshResultTimerRef.current) {
        clearTimeout(refreshResultTimerRef.current);
      }
      setRefreshResult(null);
      try {
        const [result] = await Promise.all([
          refreshBalances(connectionId),
          triggerSync(connectionId),
        ]);
        setRefreshResult({ refreshed_count: result.refreshed_count });
        refreshResultTimerRef.current = setTimeout(() => {
          setRefreshResult(null);
        }, 5000);
      } catch {
        enqueueSnackbar("Sync failed. Please try again.", { variant: "error" });
      }
    },
    [refreshBalances, triggerSync],
  );

  const handleDisconnect = useCallback(async () => {
    if (!activeConnection) return;
    try {
      await disconnect(activeConnection.id);
      setShowDisconnectConfirm(false);
      enqueueSnackbar("Account disconnected", { variant: "success" });
    } catch {
      enqueueSnackbar("Couldn't disconnect. Please try again.", { variant: "error" });
    }
  }, [disconnect, activeConnection]);

  const handleExport = useCallback(async () => {
    if (!activeConnection) return;
    setIsExporting(true);
    try {
      await generateTransactionsPdf({
        transactions: rawTransactions,
        accountName: activeConnection.institution_name ?? "Estate account",
        estateDisplayName,
        balance: moneyIn - moneyOut,
        moneyIn,
        moneyOut,
      });
    } catch {
      enqueueSnackbar("Couldn't generate PDF. Please try again.", { variant: "error" });
    } finally {
      setIsExporting(false);
    }
  }, [activeConnection, rawTransactions, estateDisplayName, moneyIn, moneyOut]);

  const steps = [
    {
      label: "Letters of authority",
      description: "Proves you can act for the estate",
      status: isRoleLoading || !isAuthorityDocFetched
        ? "upcoming" as const
        : hasAuthority ? "completed" as const : "active" as const,
      action: hasAuthority || isRoleLoading || !isAuthorityDocFetched
        ? undefined
        : {
            label: "Get authorized",
            onClick: () => (isPaidUser ? navigate(POST_LOSS_ROUTES.GET_AUTHORIZED) : onUpgrade()),
          },
    },
    {
      label: "Estate EIN",
      description: "The estate's tax ID from the IRS",
      status: hasEin
        ? ("completed" as const)
        : hasAuthority
          ? ("active" as const)
          : ("upcoming" as const),
      action:
        !hasEin && hasAuthority
          ? {
              label: "Enter EIN",
              onClick: () => setShowEinDrawer(true),
            }
          : undefined,
    },
    {
      label: "Open the account and connect it",
      description: "Bring both to any bank",
      status: hasEstateAccount
        ? ("completed" as const)
        : hasEin
          ? ("active" as const)
          : ("upcoming" as const),
      action:
        !hasEstateAccount && hasEin
          ? {
              label: "Connect",
              onClick: () => handleConnect(),
            }
          : undefined,
    },
  ];

  const syncingInstitutionName = pendingInstitution?.name ?? "Estate account";
  const syncingInstitutionLogo = pendingInstitution?.logo ?? null;

  if (isLoadingConnections) {
    return (
      <div className="flex flex-1 items-center justify-center py-16">
        <Loader size="md" />
      </div>
    );
  }

  if (connectionsError) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 py-16">
        <p className="text-sm text-secondary">Couldn't load account info</p>
        <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
          Try again
        </Button>
      </div>
    );
  }

  return (
    <div className="relative flex flex-1 flex-col min-w-0 overflow-hidden">
      {connectionStep === "exchanging" ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/60">
          <Loader size="md" />
        </div>
      ) : null}
      {connectionStep === "syncing" ? (
        <FirstSyncLoading
          accountName={syncingInstitutionName}
          institutionLogo={syncingInstitutionLogo}
        />
      ) : hasEstateAccount && activeConnection && connectionStep !== "account-selector" && connectionStep !== "exchanging" ? (
        <div className="flex min-w-0 flex-col gap-6 overflow-hidden pt-8 sm:gap-10 sm:pt-10">
          {activeConnection.status === "error" ? (
            <ReconnectBanner onReconnect={() => handleConnect(activeConnection.id)} />
          ) : null}
          <ConnectedAccountCard
            connection={activeConnection}
            estateDisplayName={estateDisplayName}
            onRefresh={() => handleRefresh(activeConnection.id)}
            onReconnect={() => handleConnect(activeConnection.id)}
            isRefreshing={isRefreshing}
            refreshResult={refreshResult}
            onDisconnect={() => setShowDisconnectConfirm(true)}
            onExport={handleExport}
            isExporting={isExporting}
          />
          {isLoadingTransactions ? (
            <div className="flex items-center justify-center py-12">
              <Loader size="md" />
            </div>
          ) : transactionsError ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12">
              <p className="text-sm text-secondary">Couldn't load transactions</p>
            </div>
          ) : (
            <>
              <FinancialSummary
                balance={moneyIn - moneyOut}
                moneyIn={moneyIn}
                moneyOut={moneyOut}
              />
              <ActivityTable
                transactions={mappedTransactions}
                onRowClick={(t) => setSelectedTransactionId(t.id)}
                onCategoryChange={(transactionId, category) => {
                  updateTransaction({ transactionId, updates: { category } }).catch(() => {
                    enqueueSnackbar("Couldn't update category. Please try again.", { variant: "error" });
                  });
                }}
              />
            </>
          )}
        </div>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center gap-8 px-4 py-8 sm:p-10">
          <div className="flex flex-col items-center gap-1">
            <h3 className="text-xl font-semibold leading-[30px] text-center">
              Open an account for {displayName}&apos;s estate
            </h3>
            <p className="w-full sm:w-[400px] text-center text-sm text-secondary">
              You&apos;ll need an estate account to deposit {displayName}&apos;s checks, pay
              outstanding bills, and keep estate money completely separate from your personal
              finances.
            </p>
          </div>

          <AccountSetupChecklist steps={steps} />

          <p className="text-center text-sm text-secondary">
            Already have an account?{" "}
            <button
              type="button"
              className="font-medium text-brand hover:underline"
              onClick={() => handleConnect()}
            >
              Connect it
            </button>
          </p>
        </div>
      )}

      <PlaidPermissionsDialog
        open={connectionStep === "permissions"}
        onOpenChange={handlePermissionsClose}
        onContinue={handlePermissionsContinue}
        isLoading={isCreatingLinkToken}
      />

      {connectionStep === "plaid-link" && linkToken ? (
        <PlaidLinkLauncher
          linkToken={linkToken}
          onSuccess={handlePlaidSuccess}
          onExit={handlePlaidExit}
        />
      ) : null}

      <AccountSelectorDialog
        open={connectionStep === "account-selector"}
        onOpenChange={handleAccountSelectorClose}
        accounts={pendingAccounts}
        onSelect={handleAccountSelect}
      />

      <ConfirmDeleteDialog
        isOpen={showDisconnectConfirm}
        onClose={() => setShowDisconnectConfirm(false)}
        onConfirm={handleDisconnect}
        title="Disconnect account"
        description={`Are you sure you want to disconnect ${activeConnection?.institution_name ?? "this account"}?`}
        isLoading={isDisconnecting}
      />

      <TransactionDetailPanel
        transaction={selectedTransaction}
        onClose={() => setSelectedTransactionId(null)}
        onUpdate={(id, updates) => updateTransaction({ transactionId: id, updates })}
        isUpdating={isUpdating}
        estateId={estateId}
      />

      {estateId ? (
        <AlreadyHaveEINDrawer
          isOpen={showEinDrawer}
          onClose={() => setShowEinDrawer(false)}
          estateId={estateId}
        />
      ) : null}
    </div>
  );
};
